import { faMapMarkerAlt as faMapMarkerAltLight } from '@fortawesome/pro-light-svg-icons/faMapMarkerAlt';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faExpand } from '@fortawesome/pro-regular-svg-icons/faExpand';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faVideoPlus } from '@fortawesome/pro-regular-svg-icons/faVideoPlus';
import GalleryIcon from '@propertypal/shared/src/resources/icons/property/gallery.svg';
import SitemapIcon from '@propertypal/shared/src/resources/icons/property/sitemap-black.svg';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import getPropertyFloorPlan from '@propertypal/shared/src/utils/property/getPropertyFloorPlan';
import getPropertyTour from '@propertypal/shared/src/utils/property/getPropertyTour';
import getPropertyVideos from '@propertypal/shared/src/utils/property/getPropertyVideos';
import IMAGE_LOCATOR from '@propertypal/web-app/src/constants/imageLocator';
import React, { FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import {
  Container,
  Content,
  DropdownButton,
  IconLink,
  IconWrapper,
  LinkButton,
  Options,
  Text,
} from './SlideshowLinks.style';

interface LinkType {
  text: string;
  url: string;
  icon?: any;
  svg?: any;
  shallow?: boolean;
  className?: string;
}

export interface SlideshowLinksProps {
  getReplaceUrl: (url: string) => string;
  property: Property;
  hasSitemap?: boolean;
}

const getListButtonLinks = (getReplaceUrl: (url: string) => string, property: Property, hasSitemap?: boolean) => {
  const propertyVideo = getPropertyVideos(property);
  const propertyTour = getPropertyTour(property);
  const propertyFloorPlans = getPropertyFloorPlan(property, IMAGE_LOCATOR);

  const links: LinkType[] = [];

  if (property.images?.length) {
    links.push({
      text: 'Gallery',
      url: getReplaceUrl('/gallery'),
      icon: undefined,
      svg: <GalleryIcon />,
      className: 'gallery-icon',
    });
  }

  links.push({
    text: 'Map',
    url: getReplaceUrl('/map'),
    icon: faMapMarkerAltLight,
  });

  // if (property.images?.length && property.type === 'property' && property.propertyType?.key === 'residential') {
  //   links.push({
  //     text: 'Shop the Look',
  //     url: getReplaceUrl('/shop-the-look'),
  //     icon: faShoppingCart,
  //   });
  // }

  if (propertyTour && propertyTour.tourIndex !== undefined) {
    links.push({
      text: 'Tour',
      url: getReplaceUrl(`/tour${propertyTour.tourIndex + 1}`),
      icon: faVideoPlus,
    });
  }

  if (propertyVideo) {
    links.push({
      text: 'Video',
      url:
        propertyVideo.tourIndex !== undefined
          ? getReplaceUrl(`/tour${propertyVideo.tourIndex + 1}`)
          : getReplaceUrl('/video'),
      icon: faVideo,
    });
  }

  if (propertyFloorPlans?.length) {
    links.push({
      text: 'Floor Plans',
      url: getReplaceUrl('/floor-plans'),
      svg: undefined,
      icon: faExpand,
      shallow: true,
    });
  }

  if (hasSitemap) {
    links.push({
      text: 'Site Map',
      url: getReplaceUrl('/sitemap'),
      svg: <SitemapIcon />,
    });
  }

  return links;
};

const ICON_STYLE = {
  color: 'inherit',
  fontSize: 28,
};

const SlideshowLinks: FunctionComponent<SlideshowLinksProps> = ({ getReplaceUrl, property, hasSitemap }) => {
  const linkButtons = getListButtonLinks(getReplaceUrl, property, hasSitemap);
  const showOptions = linkButtons.length > 4;
  const [dropdownActive, setDropdownActive] = useState(false);
  const theme = useTheme();

  const trackLink = (button: string) => () => {
    const page = property.type === 'property' ? 'PDP' : 'DDP';
    trackGaEvent('media_box_click', {
      button,
      page,
      location: page,
    });
  };

  return (
    <Container>
      <Content showOptions={showOptions}>
        {linkButtons.map((link) => (
          <LinkButton
            key={link.text}
            href={link.url}
            prefetch={false}
            shallow
            className={link.className}
            onClick={trackLink(link.text)}
          >
            {!!link.icon && <FontAwesomeIcon icon={link.icon} color={theme.textDark} size={22} />}

            {link.svg}

            <Text>{link.text}</Text>
          </LinkButton>
        ))}
      </Content>

      <Options
        showOptions={showOptions}
        style={{ maxHeight: dropdownActive ? 200 : 0, marginBottom: dropdownActive ? -15 : 0 }}
      >
        <DropdownButton onClick={() => setDropdownActive(!dropdownActive)}>
          <Text>Options</Text>
          <FontAwesomeIcon
            icon={dropdownActive ? faChevronUp : faChevronDown}
            color={theme.textDark}
            style={{ fontSize: 18, marginLeft: 20 }}
          />
        </DropdownButton>

        <IconWrapper>
          {linkButtons.map((link) => (
            <IconLink key={link.text} href={link.url} prefetch={false} shallow className={link.className}>
              {!!link.icon && <FontAwesomeIcon icon={link.icon} color={theme.textDark} style={ICON_STYLE} />}

              {link.svg}
            </IconLink>
          ))}
        </IconWrapper>
      </Options>
    </Container>
  );
};

export default SlideshowLinks;
